import { ArrowUp, ArrowDown, LucideIcon } from 'lucide-react'
import { Skeleton } from '@arco-design/web-react'

export type MetricTone = 'indigo' | 'emerald' | 'amber' | 'violet' | 'cyan' | 'slate'

export interface MetricItem {
  key: string
  title: string
  hint?: string
  value: string
  icon: LucideIcon
  tone?: MetricTone
  loading?: boolean
  trend?: {
    direction: 'up' | 'down'
    value: string
    label?: string
  }
}

const TONE_STYLES: Record<MetricTone, { bg: string; fg: string }> = {
  indigo: { bg: 'var(--brand-primary-light)', fg: 'var(--brand-primary)' },
  emerald: { bg: 'var(--color-success-bg)', fg: 'var(--color-success-text)' },
  amber: { bg: 'var(--color-warning-bg)', fg: 'var(--color-warning-text)' },
  violet: { bg: 'color-mix(in srgb, var(--brand-secondary) 12%, white)', fg: 'var(--brand-secondary)' },
  cyan: { bg: 'var(--color-info-bg)', fg: 'var(--color-info-text)' },
  slate: { bg: 'var(--color-neutral-bg)', fg: 'var(--color-neutral-text)' },
}

interface StatsSectionProps {
  metrics: MetricItem[]
}

function MetricCard({ metric }: { metric: MetricItem }) {
  const Icon = metric.icon
  const tone = metric.tone ?? 'indigo'
  const { bg, fg } = TONE_STYLES[tone]

  return (
    <article className="tt-card p-5 min-h-[128px] transition-colors hover:border-[var(--brand-primary-mid)]">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-[var(--console-text-muted)]">{metric.title}</p>
          {metric.loading ? (
            <Skeleton
              animation
              text={{ rows: 1, width: ['72px'] }}
              className="mt-2 [&_.arco-skeleton-text-row]:h-7"
            />
          ) : (
            <p className="mt-2 text-[28px] leading-none font-bold tabular-nums tracking-[-0.03em] text-[var(--console-text)] truncate">
              {metric.value}
            </p>
          )}
        </div>
        <span
          className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0"
          style={{ background: bg }}
        >
          <Icon size={18} style={{ color: fg }} strokeWidth={1.8} />
        </span>
      </div>

      {metric.hint && !metric.loading && (
        <p className="mt-4 text-[11px] text-[var(--console-text-subtle)] truncate">{metric.hint}</p>
      )}

      {metric.trend && !metric.loading && (
        <div className="mt-2 flex items-center gap-1 text-xs">
          {metric.trend.direction === 'up' ? (
            <ArrowUp size={12} className="text-[var(--color-success)]" />
          ) : (
            <ArrowDown size={12} className="text-[var(--color-danger)]" />
          )}
          <span
            className={
              metric.trend.direction === 'up'
                ? 'text-[var(--color-success)] font-medium'
                : 'text-[var(--color-danger)] font-medium'
            }
          >
            {metric.trend.value}
          </span>
          {metric.trend.label && (
            <span className="text-[var(--console-text-muted)] ml-1">{metric.trend.label}</span>
          )}
        </div>
      )}
    </article>
  )
}

export default function StatsSection({ metrics }: StatsSectionProps) {
  return (
    <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      {metrics.map((metric) => (
        <MetricCard key={metric.key} metric={metric} />
      ))}
    </section>
  )
}
